import clsx from 'clsx';
import { useFormStore } from '../../lib/store';
import { openExternal } from '../../lib/liff';
import ModuleCard from '../ModuleCard';
import { IconAlert, IconMessage } from '../Icons';
import { Notice, btnOutline } from '../ui';

interface AgreementModuleProps {
  module: {
    id: string;
    name: string;
    fields: any[];
    settings: {
      lineGroupUrl?: string;
      showQRCode?: boolean;
      qrCodeImageUrl?: string;
      warningText?: string;
      agreementText?: string;
    };
  };
  event: any;
}

export default function AgreementModule({ module, event }: AgreementModuleProps) {
  const { formData, setFormValue, errors, clearError } = useFormStore();
  const settings = module.settings || {};
  const lineGroupUrl = settings.lineGroupUrl || event.lineGroupUrl;
  const customQrCodeUrl = settings.qrCodeImageUrl || event.lineGroupQrCodeUrl;
  const qrCodeImageUrl = customQrCodeUrl || (lineGroupUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(lineGroupUrl)}`
    : null);

  const field = module.fields?.[0] || { id: 'agree_checkbox', label: '我已了解注意事項並加入群組', required: true };
  const isChecked = Boolean(formData[field.id]);
  const error = errors[field.id];

  return (
    <ModuleCard title={module.name || '報名須知'}>
      {settings.agreementText && (
        <p className="text-[14px] leading-relaxed text-[#4B5563] whitespace-pre-wrap">{settings.agreementText}</p>
      )}

      {(lineGroupUrl || qrCodeImageUrl) && (
        <div className="flex flex-col gap-3 p-4 rounded-card bg-soft">
          <div className="flex items-center gap-3">
            {qrCodeImageUrl && (
              <img src={qrCodeImageUrl} alt="LINE 群組 QR Code" className="w-[88px] h-[88px] rounded-[12px] bg-white p-1.5 object-contain shrink-0" />
            )}
            <div className="flex-1 min-w-0 flex flex-col gap-0.5 leading-snug">
              <span className="text-[15px] font-bold">加入活動 LINE 群組</span>
              <span className="text-xs text-muted">集合資訊、對帳結果都在群組公布。{qrCodeImageUrl ? '可長按 QR Code 加入。' : ''}</span>
            </div>
          </div>
          {lineGroupUrl && (
            <button type="button" onClick={() => openExternal(lineGroupUrl)} className={clsx(btnOutline, '!h-11')}>
              <IconMessage size={18} />
              開啟群組邀請
            </button>
          )}
        </div>
      )}

      <Notice tone="warn" icon={<IconAlert size={16} />}>
        {settings.warningText || '請務必加入群組，未加入而錯過重要資訊恕不負責。'}
      </Notice>

      <div id={`field-${field.id}`} className="flex flex-col gap-1.5">
        <label className={clsx(
          'flex items-start gap-3 px-4 py-3.5 rounded-[14px] cursor-pointer transition-colors',
          isChecked ? 'bg-primary-soft' : 'bg-soft',
          error && '!bg-bad-soft',
        )}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => { setFormValue(field.id, e.target.checked); if (e.target.checked) clearError(field.id); }}
            className="w-5 h-5 mt-0.5 shrink-0"
          />
          <span className="text-[15px] leading-relaxed font-medium">
            {field.label}
            {field.required && <span className="text-bad-dot ml-1" aria-hidden="true">*</span>}
          </span>
        </label>
        {error && <p role="alert" className="text-xs text-bad">{error}</p>}
      </div>
    </ModuleCard>
  );
}
